import { Controller } from "@hotwired/stimulus"
import debounce from "debounce"

export default class extends Controller {
  static targets = ["content"]
  static values = {
    upThreshold: { type: Number, default: 20 },
    downThreshold: { type: Number, default: 10 }
  }

  // Constants for scroll detection
  static SCROLL_NOISE_THRESHOLD = 1
  static MINOR_SCROLL_THRESHOLD = 5

  connect() {
    // Error checking
    if (!this.hasContentTarget) {
      console.warn('StickyHeaderController: No content target found')
      return
    }

    this.lastScrollY = window.scrollY
    this.ticking = false
    this.isHidden = false
    this.contentHeight = this.contentTarget.offsetHeight
    this.scrollDirection = null

    this.boundHandleScroll = this.handleScroll.bind(this)
    this.boundHandleResize = debounce(this.handleResize.bind(this), 100)
    
    window.addEventListener("scroll", this.boundHandleScroll, { passive: true })
    window.addEventListener("resize", this.boundHandleResize)
    
    // Set initial state
    this.updateHeaderVisibility()
  }

  disconnect() {
    window.removeEventListener("scroll", this.boundHandleScroll)
    window.removeEventListener("resize", this.boundHandleResize)
  }

  handleResize() {
    if (!this.hasContentTarget) return
    
    this.contentHeight = this.contentTarget.offsetHeight
    this.updateHeaderVisibility()
  }

  handleScroll() {
    if (!this.ticking) {
      this.ticking = true
      requestAnimationFrame(() => {
        this.updateHeaderVisibility()
        this.ticking = false
      })
    }
  }


  updateHeaderVisibility() {
    const currentScrollY = window.scrollY
    const scrollDelta = Math.abs(currentScrollY - this.lastScrollY)
    
    this.updateScrollDirection(currentScrollY)

    // Always show at the top
    if (currentScrollY <= this.upThresholdValue) {
      this.showHeader()
    }
    // Hide when scrolling down past content height with sufficient delta
    else if (this.shouldHideHeader(currentScrollY, scrollDelta)) {
      this.hideHeader()
    }
    // Show when scrolling up - more lenient conditions
    else if (this.shouldShowHeader(currentScrollY, scrollDelta)) {
      this.showHeader()
    }

    this.lastScrollY = currentScrollY
  }

  updateScrollDirection(currentScrollY) {
    // Determine scroll direction with a minimum threshold to avoid noise
    if (currentScrollY > this.lastScrollY + this.constructor.SCROLL_NOISE_THRESHOLD) {
      this.scrollDirection = 'down'
    } else if (currentScrollY < this.lastScrollY - this.constructor.SCROLL_NOISE_THRESHOLD) {
      this.scrollDirection = 'up'
    }
  }

  shouldHideHeader(currentScrollY, scrollDelta) {
    return this.scrollDirection === 'down' && 
           currentScrollY > this.contentHeight && 
           scrollDelta >= this.downThresholdValue &&
           !this.isHidden
  }

  shouldShowHeader(currentScrollY, scrollDelta) {
    return this.scrollDirection === 'up' && 
           (scrollDelta >= this.upThresholdValue || currentScrollY < this.lastScrollY - this.constructor.MINOR_SCROLL_THRESHOLD) &&
           this.isHidden
  }

  showHeader() {
    if (this.element) {
      this.element.style.transform = 'translateY(0px)'
      this.isHidden = false
    }
  }

  hideHeader() {
    if (this.element) {
      this.element.style.transform = `translateY(calc(-${this.contentHeight}px + (var(--spacing) * 1.5)))`
      this.isHidden = true
    }
  }

}